import { Check, Minus, type LucideIcon } from 'lucide-react'
import type { MouseEvent as ReactMouseEvent } from 'react'

import { cn, FOCUS_RING } from '@/components/base'
import {
  readClickModifiers,
  type ClickModifiers,
} from '@/utils/click-modifiers'

/**
 * The bare mark. `PickerRow` draws it in a row that is itself the tick;
 * `SelectionCheckbox` below wraps it in its own button, for a row that is
 * pressed for something else — so a tick in a dialog and a tick in a table are
 * the same mark.
 *
 * @single-use `PickerRow` is the row that has to be the checkbox itself; a row
 * that only carries one uses `SelectionCheckbox`
 */
export function SelectionBox({
  checked,
  indeterminate,
  muted,
  shape = 'box',
  glyph: Glyph = Check,
}: {
  checked: boolean
  indeterminate?: boolean
  /** Something is in the way of this row — a dashed edge where a solid one
   *  would say "free to tick". */
  muted?: boolean
  /** What a ticked box shows instead of the check — for a tick whose meaning
   *  is not "chosen" and has a glyph of its own. */
  glyph?: LucideIcon
  /**
   * `radio` for a choice that excludes its siblings — the round mark and the
   * dot, at the box's size and in its tokens. A surface that lets the reader
   * pick one of several has to say so before they press.
   */
  shape?: 'box' | 'radio'
}) {
  const filled = checked || indeterminate

  return (
    <span
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center border transition-colors',
        shape === 'radio' ? 'rounded-full' : 'rounded',
        filled
          ? 'border-primary bg-primary text-primary-foreground'
          : muted
            ? 'border-dashed border-border bg-card'
            : 'border-border bg-card',
      )}
    >
      {shape === 'radio' ? (
        filled ? (
          <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
        ) : null
      ) : indeterminate ? (
        <Minus size={12} strokeWidth={3} />
      ) : checked ? (
        <Glyph size={12} strokeWidth={3} />
      ) : null}
    </span>
  )
}

export function SelectionCheckbox({
  checked,
  onToggle,
  shape = 'box',
  disabled,
  label,
  glyph,
}: {
  /** `indeterminate` is the header's tri-state: some rows ticked, not all. */
  checked: boolean | 'indeterminate'
  /** Receives the modifier keys so a caller can extend a range on shift or
   *  add to the selection on ctrl/cmd. */
  onToggle: (modifiers: ClickModifiers) => void
  /** `radio` where the choice excludes its siblings. */
  shape?: 'box' | 'radio'
  /** A rule holds the choice where it is, or the row is not offered at all. */
  disabled?: boolean
  /** What the tick stands for, where the row's own text is not beside it. */
  label?: string
  /** What a ticked box shows instead of the check. */
  glyph?: LucideIcon
}) {
  const isChecked = checked === true
  const isIndeterminate = checked === 'indeterminate'

  return (
    <button
      type="button"
      role={shape === 'radio' ? 'radio' : 'checkbox'}
      aria-checked={isIndeterminate ? 'mixed' : isChecked}
      aria-label={label}
      disabled={disabled}
      onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
        // Without this the surrounding row opens as well as ticking.
        event.stopPropagation()
        onToggle(readClickModifiers(event))
      }}
      // `flex`, so the box is not an inline box on the row's baseline: the
      // descender space under it would leave the tick sitting above the centre
      // of every row it ticks.
      className={cn(
        'flex',
        shape === 'radio' ? 'rounded-full' : 'rounded',
        FOCUS_RING,
      )}
    >
      <SelectionBox
        checked={isChecked}
        indeterminate={isIndeterminate}
        muted={disabled}
        shape={shape}
        glyph={glyph}
      />
    </button>
  )
}
